import React from "react";
import { BookOpen, Users, Phone, BarChart3, Calendar, Upload, Download, MessageSquare, Target, Lightbulb } from "lucide-react";

const sections = [
  {
    icon: BarChart3, title: "Dashboard",
    steps: [
      "Navigate to Admin > CRM Dashboard to see your overview",
      "View total clients, pipeline funnel, lead sources, and follow-up reminders",
      "The 'Needs Follow-up' section shows clients not contacted in 3+ days",
      "Upcoming appointments are listed with dates and client names",
    ],
  },
  {
    icon: Users, title: "Client Management",
    steps: [
      "Go to CRM > Clients to view all your contacts",
      "Click '+ Add Client' to add a new lead manually",
      "Use the search bar to find clients by name, email, phone, or company",
      "Filter by Source (Facebook, Cold Call, Referral, Website, Manual) or Pipeline Stage",
      "Click the edit icon to update client info, or trash icon to delete",
      "Change pipeline stage directly from the table dropdown",
    ],
  },
  {
    icon: Target, title: "Sales Pipeline (Kanban)",
    steps: [
      "Go to CRM > Pipeline to see all clients organized by stage",
      "5 stages: New Lead → Contacted → Negotiation → Won / Lost",
      "Drag and drop client cards between columns to move them through the pipeline",
      "Each card shows the client name, company, source, and quick contact icons",
      "Click the phone or email icon on a card to contact them directly",
    ],
  },
  {
    icon: Calendar, title: "Appointments",
    steps: [
      "Go to CRM > Appointments to manage all scheduled meetings",
      "Click '+ Schedule' to book a new appointment with a client",
      "Set the date, time, duration, and add notes",
      "Mark appointments as completed or cancelled with the action buttons",
      "Click the link icon to add the appointment to Google Calendar",
      "Delete old appointments with the trash icon",
    ],
  },
  {
    icon: Phone, title: "Logging Interactions",
    steps: [
      "When viewing a client's details, you can log calls, messages, meetings, and emails",
      "Each interaction records the type, notes, and outcome",
      "This history helps you track all communications with each client",
      "The 'last contact' date updates automatically when you log an interaction",
    ],
  },
  {
    icon: Upload, title: "CSV Import",
    steps: [
      "Go to CRM > Clients and click 'Import CSV'",
      "Your CSV file should have these columns: name, email, phone, company, source, notes, pipeline_stage",
      "Valid sources: facebook, cold_call, referral, website, manual",
      "Valid pipeline stages: new_lead, contacted, in_negotiation, closed_won, closed_lost",
      "The system will import all rows with a name",
    ],
  },
  {
    icon: Download, title: "CSV Export",
    steps: [
      "Go to CRM > Clients and click 'Export'",
      "A CSV file will download with all your client data",
      "You can open it in Excel, Google Sheets, or any spreadsheet app",
      "Use this for backups or to share data with your team",
    ],
  },
  {
    icon: MessageSquare, title: "Quick Actions",
    steps: [
      "Use the floating action button (bottom-right) for quick access to common tasks",
      "Log a Call: Quickly record a phone conversation with a client",
      "Add Lead: Add a new client without leaving the current page",
      "Schedule Follow-up: Book an appointment in seconds",
      "Quick Note: Add a note to a client's record",
    ],
  },
];

const fbTips = [
  { title: "Respond within 5 minutes", desc: "Facebook shows 'Very responsive' badges to businesses that reply quickly. This builds trust." },
  { title: "Use the CRM to track every lead", desc: "When someone messages you on Marketplace, add them as a client with source 'Facebook' immediately." },
  { title: "Move leads to your website", desc: "In your replies, share a link to the specific forklift detail page on your site for a professional touch." },
  { title: "Boost listings strategically", desc: "Boost your best-value forklifts. Use the CRM pipeline to track which boosted ads generate the most leads." },
  { title: "Follow up within 24 hours", desc: "If a lead doesn't reply, log it in the CRM and set a follow-up appointment for the next day." },
  { title: "Tag the source consistently", desc: "Always mark Facebook leads with the 'Facebook' source in the CRM so you can track conversion rates." },
  { title: "Use photos from your inventory", desc: "Your Fork Finder photos are already professional — use them in your Marketplace listings for consistency." },
];

export default function CRMGuide() {
  return (
    <div data-testid="crm-guide" className="p-8 max-w-4xl">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 bg-yellow-400 rounded-sm flex items-center justify-center">
          <BookOpen className="w-5 h-5 text-gray-900" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">CRM User Guide</h1>
          <p className="text-sm text-gray-500">Step-by-step instructions for every feature</p>
        </div>
      </div>

      <div className="space-y-8">
        {sections.map((section, i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <section.icon className="w-5 h-5 text-yellow-500" />
              <h2 className="font-heading text-lg font-bold text-gray-900">{section.title}</h2>
            </div>
            <ol className="space-y-2">
              {section.steps.map((step, j) => (
                <li key={j} className="flex items-start gap-3 text-sm text-gray-700">
                  <span className="font-mono text-xs font-bold text-yellow-600 bg-yellow-50 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">{j + 1}</span>
                  {step}
                </li>
              ))}
            </ol>
          </div>
        ))}

        {/* Facebook Marketplace Tips */}
        <div className="bg-white border border-gray-200 rounded-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <Lightbulb className="w-5 h-5 text-blue-500" />
            <h2 className="font-heading text-lg font-bold text-gray-900">Facebook Marketplace Tips</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">Best practices for connecting your Marketplace ads to the CRM workflow:</p>
          <div className="space-y-4">
            {fbTips.map((tip, i) => (
              <div key={i} className="border-l-2 border-blue-400 pl-4">
                <p className="text-sm font-semibold text-gray-900">{tip.title}</p>
                <p className="text-sm text-gray-600">{tip.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
